'use client';

import React, { useEffect } from 'react';

import ErrorBoundaryIcon from '../../src/asset/svg/error-boundary-icon.svg';
import { addRenderingErrorToRum } from '../utils/error/addRenderingErrorToRum';
import useChunkLoadErrorRecovery from '../utils/hook/useChunkLoadErrorRecovery';
import { useHistoryMethod } from '../utils/hook/useHistoryMethod';
import { usePathnameMethod } from '../utils/hook/usePathnameMethod';
import useShowChannelTalkMessenger from '../utils/hook/useShowChannelTalkMessenger';

/**
 * @link https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
const ErrorBoundaryPage = (props: {
  error: Error & { digest?: string };
  reset: () => void;
}) => {
  const { error, reset } = props;
  const history = useHistoryMethod();
  const { pathname } = usePathnameMethod();
  const { showChannelTalkMessenger } = useShowChannelTalkMessenger();

  const { isChunkLoadError, isRecovering } = useChunkLoadErrorRecovery(error);

  /**
   * history.push와 함께 props.reset()시도시 다시 에러가 발생하게 됩니다.
   * -> 이를 회피하기 위해서 reset을 pathname변경으로 useEffect가 clean-up 될때 props.reset을 시도합니다.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname 변경 시 cleanup(reset) 재실행 의도
  useEffect(() => {
    // 청크 로드 실패는 useChunkLoadErrorRecovery가 자동 복구 처리하므로 RUM 로깅을 생략한다.
    if (!isChunkLoadError) {
      addRenderingErrorToRum({ error, source: 'ErrorBoundaryPage.tsx' });
    }

    return () => {
      reset(); // 에러난 pathname에서 이탈시 에러 초기화
    };
  }, [pathname, error, reset, isChunkLoadError]);

  // 청크 에러 복구 진행 중에는 "서비스 오류" 화면 플래시를 막기 위해 빈 화면을 렌더한다.
  if (isRecovering) {
    return null;
  }

  return (
    <div className="max-w-480 flex-center grow">
      <div className="flex flex-col items-center space-y-28px">
        <img src={ErrorBoundaryIcon.src} alt="에러 발생" />

        <div className="space-y-12px">
          <h2 className="text-26px font-bold text-gray-800">
            서비스 오류 발생
          </h2>
          <h5 className="text-center text-16px leading-6 text-gray-800">
            불편을 드려 죄송합니다.
            <br />
            고객센터로 문의해주세요.
          </h5>
        </div>

        <div className="flex space-x-10px">
          <button
            onClick={history.back}
            className="h-56px w-108px rounded-10px bg-gray-600"
            type="button"
          >
            <h5 className="text-16px font-semibold text-white">이전</h5>
          </button>
          <button
            onClick={showChannelTalkMessenger}
            className="h-56px w-108px rounded-10px bg-gray-500"
            type="button"
          >
            <h5 className="text-16px font-semibold text-white">문의하기</h5>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundaryPage;
